// ---------------------------------------------------------------------------
// Everything the charts derive from the raw rows.
//
// Deliberately free of Vue and of the DOM, so `node --test` can run it against
// the real dataset (cases.test.js). The rules below are what separates these
// charts from the Power BI prototype, and they are the kind of thing that goes
// quietly wrong on the next data refresh — hence the check.
//
// The unit rule, from docs/uohs-dashboard.md: anything counting CASES collapses
// rows by `id` first, anything counting MONEY stays on rows, because every row
// is a real contract with its own price. Each function below says which it is.
// ---------------------------------------------------------------------------

import { max, mean, median, sum } from 'd3-array'

const DAY = 24 * 60 * 60 * 1000
const ORDER = 'příkaz'
const ON_REQUEST = 'na návrh'

// Every detail URL follows from the id, which is why build_dataset drops the
// column: 3,571 copies of the same prefix are 264 kB of nothing.
export const DETAIL_URL = 'https://uohs.gov.cz/cs/verejne-zakazky/sbirky-rozhodnuti/detail-'

export const detailUrl = (id) => `${DETAIL_URL}${id}.html`

/**
 * One entry per decision of the Office.
 *
 * A row is a contract; an `id` is a decision. One decision can cover several
 * contracts, and those duplicate rows differ only in `cena`. Anything counting
 * cases has to collapse them first, otherwise it counts contracts and labels
 * them cases — which is what the prototype does.
 */
export function decisions(rows) {
    const byId = new Map()

    for (const row of rows) {
        if (!byId.has(row.id)) byId.set(row.id, row)
    }

    return [...byId.values()]
}

/**
 * Decisions with orders left out — the base the prototype calls "final decisions".
 *
 * Orders (`příkaz`) carry no start date at all: every one of them has
 * `datum_zahajeni_rizeni` empty, so nothing about their timing can be charted,
 * and they never go to a second instance either.
 */
export function finalDecisions(rows) {
    return decisions(rows).filter((row) => row.typ_zahajeni_rizeni !== ORDER)
}

/** Whole days between two ISO dates, or null if either is missing. */
function span(from, to) {
    if (!from || !to) return null

    // Date.parse of an ISO date yields UTC midnight, so the difference is a
    // whole number of days with no daylight-saving remainder.
    return (Date.parse(to) - Date.parse(from)) / DAY
}

const duration = (row) => span(row.datum_zahajeni_rizeni, row.datum_pravni_moci)

/** Durations that are actually usable, i.e. present and not running backwards. */
const usable = (values) => values.filter((value) => value !== null && value >= 0)

// --- 2. Rozdělení délek ----------------------------------------------------

// Cases are counted in whole months, and the average month is 30.44 days long.
export const MONTH_DAYS = 30.44

/**
 * How long cases take, in months, plus the counts the chart has to admit to.
 *
 * Anything over a year lands in one bucket at the end: past that point the
 * exact month stops being the interesting part, and a reader wants to know how
 * many cases run long rather than whether one took fourteen months or sixteen.
 *
 * A case with no start date and a case that ends before it begins are two
 * different failures and are counted separately: the first is a gap in the
 * source, the second is a typo in it. Neither has a length to chart.
 */
export function summarise(rows) {
    const cases = finalDecisions(rows)
    const lengths = cases.map(duration)
    const days = usable(lengths)

    const months = Array.from({ length: 12 }, () => 0)
    let overYear = 0

    for (const length of days) {
        if (length > 365) {
            overYear += 1
            continue
        }

        months[Math.min(12, Math.max(1, Math.ceil(length / MONTH_DAYS))) - 1] += 1
    }

    return {
        total: cases.length,
        counted: days.length,
        unknown: lengths.filter((value) => value === null).length,
        impossible: lengths.filter((value) => value !== null && value < 0).length,
        average: Math.round(mean(days) ?? 0),
        median: median(days) ?? 0,
        longest: max(days) ?? 0,
        months,
        overYear
    }
}

// --- 3. Cesta případu ------------------------------------------------------

/**
 * How far a case gets. Cases, so decisions.
 *
 * The chain — first instance, appeal, third round and beyond — is genuinely
 * nested: each step is a subset of the one above it. The courts are not part of
 * that chain and cannot be drawn as its last step. Most cases that reach a court
 * have been round three times as well, and a handful go straight there from the
 * first decision, so a diagram that branches "either another round or a court"
 * would be telling a story the data does not support.
 */
export function funnel(rows) {
    const cases = finalDecisions(rows)
    const reached = (instances) => cases.filter((row) => row['počet_instanci'] >= instances).length
    const court = cases.filter((row) => row.je_soud)

    // Whether a case came round again is the source's call, not ours. Counting
    // three instances would be one case short: the August 2026 export marks one
    // that came back after the second round, and no case with three instances
    // goes unmarked. A rule we infer is a rule we get to be wrong about.
    const pingpong = (row) => row.je_pingpong

    return {
        total: cases.length,
        settled: cases.length - reached(2),
        second: reached(2),
        third: cases.filter(pingpong).length,
        court: court.length,
        // What makes the courts an aside rather than a step.
        courtAfterThird: court.filter(pingpong).length,
        courtFromFirst: court.filter((row) => row['počet_instanci'] < 2).length
    }
}

// --- 4. Kdo a co -----------------------------------------------------------

/** Rank a keyed tally, keeping the top few and folding everything else into a residual. */
function rank(tally, keep) {
    const entries = [...tally].sort((a, b) => b[1] - a[1])
    const head = entries.slice(0, keep)
    const tail = entries.slice(keep)

    return {
        items: head.map(([label, value]) => ({ label, value })),
        rest: sum(tail, ([, value]) => value),
        restCount: tail.length
    }
}

/**
 * Money by sector, so rows: each row is a contract with its own price.
 *
 * `cpv_category` is empty on 1,100 of them. That is a third of the dataset and
 * it is reported as its own figure rather than quietly dropped — a chart that
 * silently excludes a third of its base is a chart that lies.
 */
export function sectorVolume(rows, keep = 6) {
    const tally = new Map()
    let unknown = 0
    let unknownRows = 0

    for (const row of rows) {
        if (row.cena === null) continue

        if (row.cpv_category === null) {
            unknown += row.cena
            unknownRows += 1
            continue
        }

        tally.set(row.cpv_category, (tally.get(row.cpv_category) ?? 0) + row.cena)
    }

    const priced = rows.filter((row) => row.cena !== null)

    return {
        ...rank(tally, keep),
        unknown,
        unknownRows,
        total: sum(priced, (row) => row.cena),
        contracts: priced.length,
        missingPrice: rows.length - priced.length
    }
}

/**
 * The priciest contracts. Money, so rows: each row is one contract.
 *
 * No collapsing by `id` here — two contracts covered by the same decision are
 * two contracts, and this table is about what was bought.
 */
export function topContracts(rows, keep = 20) {
    return rows
        .filter((row) => row.cena !== null)
        .sort((a, b) => b.cena - a.cena)
        .slice(0, keep)
}

/**
 * What became of the money in a market, as three shares of one whole.
 *
 * The whole is the market — every contract awarded under the act, from the
 * Institute's own tally of the bulletin. Out of it, what the Office settled in
 * the first instance, what went further, and what it never saw. `rows` are
 * contracts, from contracts.json, not decisions.
 *
 * The two Office shares are a floor: a contract with no price in the source
 * cannot be counted, and `missingPrice` says how many that is.
 */
/**
 * How likely a contract of a given size is to end up before the Office.
 *
 * The Office's own share of each band: how many contracts of that size it
 * reviewed, out of how many were awarded. Both sides count contracts with a
 * price, because a contract without one has no band to fall in.
 */
export function bandShare(rows, table, filters = {}) {
    const years = list(filters.rok)
    const sectors = list(filters.obor)

    const wanted = (table ?? []).filter(([year, sector]) =>
        (!years.length || years.includes(year)) && (!sectors.length || sectors.includes(sector)))

    const office = sizeBands(rows)

    return MARKET_BANDS.map((band, index) => {
        const market = wanted
            .filter((row) => row[MARKET.band] === band)
            .reduce((total, row) => total + row[MARKET.contracts], 0)

        return {
            market,
            office: office[index].contracts,
            share: market ? office[index].contracts / market : 0
        }
    })
}

export function marketFate(rows, table, filters = {}) {
    const years = list(filters.rok)
    const sectors = list(filters.obor)

    const whole = (table ?? [])
        .filter(([year, sector]) =>
            (!years.length || years.includes(year)) && (!sectors.length || sectors.includes(sector)))
        .reduce((total, row) => total + row[MARKET.volume], 0)

    const priced = rows.filter((row) => row.cena !== null)

    const first = sum(priced.filter((row) => row.instanci < 2), (row) => row.cena)
    const second = sum(priced.filter((row) => row.instanci >= 2), (row) => row.cena)

    return {
        whole,
        first,
        second,
        rest: Math.max(whole - first - second, 0),
        missingPrice: rows.length - priced.length
    }
}

/**
 * The busiest contracting authorities, counted in cases, so decisions.
 *
 * Grouped by registration number, not by name. The source writes the roads
 * directorate as "ČR" and as "s. p." and the railways once with "a.s." and once
 * without; by name each of those is two authorities and neither is the size it
 * should be. The number says they are one body, which is a fact about the world
 * rather than a guess this file would have to make about strings.
 *
 * The name is still what a reader sees, so the spelling the source used most for
 * that number is the one that labels the bar.
 */
export function topAuthorities(rows, keep = 10) {
    const tally = new Map()
    const names = new Map()
    let unknown = 0

    for (const row of finalDecisions(rows)) {
        if (row.zadavatel === null) {
            unknown += 1
            continue
        }

        // A handful of decisions name an authority the export gave no number to.
        // Those group by name, which is all there is to go on.
        const key = row.ico ?? row.zadavatel

        tally.set(key, (tally.get(key) ?? 0) + 1)

        const spellings = names.get(key) ?? new Map()

        spellings.set(row.zadavatel, (spellings.get(row.zadavatel) ?? 0) + 1)
        names.set(key, spellings)
    }

    const spelt = (key) =>
        [...names.get(key)].sort((a, b) => b[1] - a[1])[0][0]

    const ranked = rank(tally, keep)

    return {
        ...ranked,
        items: ranked.items.map((item) => ({ ...item, label: spelt(item.label) })),
        unknown,
        distinct: tally.size
    }
}

// Round thresholds in CZK: 10 million, 100 million, 1 billion. The prototype
// cuts its bands somewhere else — it reports 916 contracts in the lowest band
// where these thresholds give 839 — but it never says where, so this uses
// numbers a reader can check.
export const PRICE_BANDS = [1e7, 1e8, 1e9]

/**
 * Contract counts and typical duration per price band.
 *
 * Two panels sharing one x axis, never two y axes on one plot: in the prototype
 * a line of average durations crosses a bar chart of counts, and the reader
 * gives the crossing points a meaning they do not have.
 *
 * Counts are contracts (money, so rows). The duration shown against them is the
 * duration of the case each contract belongs to.
 */
export function sizeBands(rows) {
    const edges = [0, ...PRICE_BANDS, Infinity]

    return edges.slice(0, -1).map((low, index) => {
        const high = edges[index + 1]
        const inBand = rows.filter((row) => row.cena !== null && row.cena >= low && row.cena < high)
        const days = usable(inBand.map(duration))

        return {
            low,
            high,
            contracts: inBand.length,
            volume: sum(inBand, (row) => row.cena),
            counted: days.length,
            median: median(days) ?? 0
        }
    })
}

// --- 1. Jak dlouho to trvá -------------------------------------------------

/**
 * The three durations the prototype puts on its scoreboard, as means over
 * decisions.
 *
 * The Office publishes its own figure for each of these, measured with the
 * clock stopped while proceedings are suspended. That number is nowhere in this
 * dataset — no column carries it — so it arrives from the template as a quoted
 * figure, and the gap between the two is the whole point of the page.
 */
export function timers(rows) {
    const all = decisions(rows)
    const cases = all.filter((row) => row.typ_zahajeni_rizeni !== ORDER)
    const orders = all.length - cases.length

    const firstInstance = (subset) =>
        usable(subset.map((row) => span(row.datum_zahajeni_rizeni, row.datum_rozhodnuti)))

    const stat = (values) => ({ days: Math.round(mean(values) ?? 0), counted: values.length })

    const started = firstInstance(cases)

    return {
        // The headline: from the start of proceedings to the decision becoming
        // final. Orders are out, because they have no start date to measure from.
        hero: stat(usable(cases.map(duration))),

        // An order is issued without proceedings, so it takes no time rather than
        // an unknown amount of it. Counting them as zero reproduces the
        // prototype's 43 from its own 85, which is what makes this reading of
        // the column the likely one.
        overall: stat([...started, ...Array(orders).fill(0)]),

        firstInstance: stat(started),
        onRequest: stat(firstInstance(cases.filter((row) => row.typ_zahajeni_rizeni === ON_REQUEST))),
        appeal: stat(
            usable(cases.map((row) => span(row.datum_podani_rozkladu, row.datum_rozhodnuti_rozkladu)))
        )
    }
}

// --- 5. Data ---------------------------------------------------------------

// Semicolon, not comma: the readers of this site open a .csv in Excel with a
// Czech locale, where the comma is a decimal separator and a comma-delimited
// file lands in one column. The BOM is there for the same reason — without it
// Excel reads the file as the local code page and mangles every diacritic.
const SEPARATOR = ';'
const BOM = '﻿'

/** Quote a cell only when it needs it, doubling any quote inside — RFC 4180. */
function cell(value) {
    if (value === null || value === undefined) return ''

    const text = Array.isArray(value) ? value.join(' ') : String(value)

    return /["\n\r;]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text
}

/**
 * The rows as they stand, plus the detail URL the dataset does not store.
 *
 * Whatever the reader filtered down to is what they get: a download that
 * silently hands back the whole file would make the filter a lie.
 */
export function toCsv(rows) {
    if (!rows.length) return ''

    const columns = [...Object.keys(rows[0]), 'url']

    const line = (row) =>
        columns.map((column) => cell(column === 'url' ? detailUrl(row.id) : row[column]))
            .join(SEPARATOR)

    return BOM + [columns.join(SEPARATOR), ...rows.map(line)].join('\r\n')
}

// --- The shared filter -----------------------------------------------------

// A pipe, not a comma: two sector names contain a comma of their own
// ("Oděvy, obuv a jiné vybavení"), so a comma-joined query string would split
// them in half. No value in the data contains a pipe.
const JOIN = '|'

// Rows with no sector still have to be selectable, or a third of the dataset
// becomes unreachable from the filter bar.
// market.json is [rok, obor, velikostní pásmo, počet, objem]. Named rather than
// indexed by hand, because a fifth column was added once already.
/**
 * A sum of money in the unit that suits its size.
 *
 * Everything on this page used to be printed in billions, which is right for a
 * market and absurd for one contract: the twenty dearest in office supplies run
 * from 250 million down, and the bottom of that table read "0 mld. Kč" twice.
 *
 * `units` is [billions, millions, thousands] and comes from the template, so the
 * words stay translatable. Two decimals below ten of a unit, none above — the
 * point is the magnitude, not the crowns.
 */
export function money(value, units) {
    const [billions, millions, thousands] = units
    const say = (amount, unit) =>
        `${amount.toLocaleString('cs-CZ', { maximumFractionDigits: amount < 10 ? 2 : 0 })} ${unit}`

    if (Math.abs(value) >= 1e9) return say(value / 1e9, billions)
    if (Math.abs(value) >= 1e6) return say(value / 1e6, millions)

    return say(value / 1e3, thousands)
}

export const MARKET = { year: 0, sector: 1, band: 2, contracts: 3, volume: 4 }

// The bands the market table is cut into, low to high — the same thresholds as
// PRICE_BANDS, written the way the source writes them. Order is the join.
export const MARKET_BANDS = ['do 10 mil.', 'do 100 mil.', 'do 1 mld.', 'nad 1 mld.']

/**
 * The filter as far as a source outside this dataset can follow it.
 *
 * Two things on the page are not ours: the market tally of the bulletin, and
 * the durations the Office publishes in its annual reports. Both know a year
 * and a sector. Neither knows whether a decision was an order or whether the
 * case later went to court — those happen inside the Office's own file.
 *
 * Anything set beside such a source has to be narrowed the same way, or the two
 * sides stop being comparable. Narrowing only ours made the ring claim 1,105
 * contracts of 93,093 where the real figure is 1,664, and made the published
 * 42 days sit next to our 40 as though the Office were slower than the calendar.
 */
export const outsideFilters = (filters) => ({ rok: filters.rok, obor: filters.obor })

export const NO_SECTOR = 'neurčeno'

/** The year the decision became final — the period the prototype filters by. */
const year = (row) => row.datum_pravni_moci?.slice(0, 4) ?? ''

const sector = (row) => row.cpv_category ?? NO_SECTOR

const list = (value) => (value ? String(value).split(JOIN).filter(Boolean) : [])

export const packFilter = (values) => values.join(JOIN)

export const unpackFilter = list

/**
 * Everything the filter bar can offer, taken from the data rather than hardcoded.
 *
 * The residual sorts last however the collator would rank it: "neurčeno" is not
 * a sector, it is the absence of one.
 */
export function facets(rows) {
    const years = [...new Set(rows.map(year))].filter(Boolean).sort()

    const sectors = [...new Set(rows.map(sector))]
        .filter((name) => name !== NO_SECTOR)
        .sort((a, b) => a.localeCompare(b, 'cs'))

    return { years, sectors: [...sectors, NO_SECTOR] }
}

/**
 * The rows the whole page is looking at.
 *
 * An empty dimension means "no restriction", not "nothing matches" — otherwise
 * the first click on any checkbox would have to be preceded by ticking all the
 * others.
 */
/**
 * The contracts the page is looking at.
 *
 * Same dimensions as visibleRows, over the other dataset. A contract counts as
 * an order only if it never was anything else — the conversion decided that, so
 * the flag is read rather than derived here.
 */
export function visibleContracts(rows, filters) {
    return narrowContracts(rows, filters).filter((row) => {
        const court = filters.soud

        if (court === 'ano' && !row.je_soud) return false
        if (court === 'ne' && row.je_soud) return false

        return !row.prikaz
    })
}

/** Year and sector alone — the two dimensions an outside source can follow. */
export function narrowContracts(rows, filters) {
    const years = list(filters.rok)
    const sectors = list(filters.obor)

    return rows.filter((row) => {
        if (years.length && !years.includes(year(row))) return false
        if (sectors.length && !sectors.includes(sector(row))) return false

        return true
    })
}

/** The same two dimensions over the decisions. Orders survive this one. */
export function narrowRows(rows, filters) {
    const years = list(filters.rok)
    const sectors = list(filters.obor)

    if (!years.length && !sectors.length) return rows

    return rows.filter((row) => {
        if (years.length && !years.includes(year(row))) return false
        if (sectors.length && !sectors.includes(sector(row))) return false

        return true
    })
}

export function visibleRows(rows, filters) {
    return narrowRows(rows, filters).filter((row) => {
        const court = filters.soud

        if (court === 'ano' && !row.je_soud) return false
        if (court === 'ne' && row.je_soud) return false

        // Orders are half the file and a different kind of thing: no proceedings
        // are held, so they have no start date and no duration. They are out of
        // every chart here, and that is not a setting — a reader who switched
        // them on got figures diluted by rows that cannot be measured at all.
        return row.typ_zahajeni_rizeni !== ORDER
    })
}
